import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TraceLink from '../agent-common/TraceLink';
import type { CatalogEntry, LadderAnswer } from '../Ladder/claims';
import useVoyagerStore from '../../stores/useVoyagerStore';
import useStudioStore from '../../stores/useStudioStore';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';

/** The card's one listing rule (#761): every entry is in the card; past this many the rest wait behind Show all. */
export const COLLAPSE_AFTER = 25;
const KIND: Record<CatalogEntry['kind'], string> = {
  perspective: 'Perspective', metric: 'Metric', dimension: 'Dimension', table: 'Table', definition: 'Definition',
};

/** Where an entry opens: the answer's pick when the entry lives there, else the first perspective that holds it. */
export function homeOf(entry: CatalogEntry, pick?: string | null): string | null {
  if (entry.kind === 'perspective') return entry.id;
  const homes = entry.perspectives.map((w) => w.id);
  return (pick && homes.includes(pick) ? pick : homes[0]) || null;
}

/** What Open in Query Builder selects: a metric by its qualified id, a dimension by its name; else the perspective. */
export function querySpec(entry: CatalogEntry, pick?: string | null) {
  const perspective = homeOf(entry, pick);
  if (!perspective) return null;
  return {
    perspective,
    metrics: entry.kind === 'metric' ? [entry.id] : [],
    dimensions: entry.kind === 'dimension' ? [entry.id.split('.').slice(1).join('.')] : [],
    filters: [],
  };
}

/**
 * View in Studio (#566):
 * - a captured definition: Studio › Definitions, with the definition selected;
 * - anything else: Studio's catalog of perspectives.
 */
export async function viewInStudio(entry: CatalogEntry, pick?: string | null): Promise<void> {
  const store = useVoyagerStore.getState();
  const studio = useStudioStore.getState();
  if (entry.kind === 'definition') {
    useVoyagerStore.setState({ selectedDefinitionId: entry.id, definitionsFilter: 'All', definitionsSearch: '' } as any);
    studio.setStudioHome('definitions'); store.navigateToStudio();
    return;
  }
  studio.setStudioHome('perspectives'); store.navigateToStudio();
}

/** #1818: an entry a reading of a meaning no person has settled covers — written by the server for this reader. */
type Unsettled = { state: 'open' | 'provisional' | 'kept_both'; others: string[] };

/** The mark's words: the other readings, when the reader may read them. */
export function unsettledText(decision: Unsettled): string {
  return decision.others.length ? `Not settled: also read as ${decision.others.join(', ')}` : 'Not settled';
}

function EntryRow({ entry, pick }: { entry: CatalogEntry; pick?: string | null }) {
  const navigateToWorkspaceWithSpec = useVoyagerStore((s) => s.navigateToWorkspaceWithSpec);
  const spec = querySpec(entry, pick);
  const homes = entry.perspectives.map((w) => w.name);
  const decision = (entry as CatalogEntry & { decision?: Unsettled | null }).decision;
  return (
    <Box data-testid={`catalog-entry-${entry.id}`} sx={{ py: 1, borderTop: 1, borderColor: 'divider', display: 'grid', gap: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.name}</Typography>
        <Chip size="small" variant="outlined" label={KIND[entry.kind]} />
        {entry.status === 'draft' && <Chip size="small" color="warning" label="Draft" data-testid="catalog-draft" />}
        {decision && <Chip size="small" variant="outlined" color="warning" label={unsettledText(decision)}
                           data-testid="catalog-unsettled" />}
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{entry.id}</Typography>
      </Stack>
      {entry.description && <Typography variant="body2" color="text.secondary">{entry.description}</Typography>}
      {entry.formula && <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{entry.formula}</Typography>}
      <Typography variant="caption" color="text.secondary">
        {[entry.kind !== 'perspective' && homes.length ? `In ${homes.join(', ')}` : null,
          entry.owner ? `Owner: ${entry.owner}` : null, entry.refresh ? `Refreshed ${entry.refresh}` : null,
          entry.version ? `Version ${entry.version}` : null].filter(Boolean).join(' · ')}
      </Typography>
      <Stack direction="row" spacing={1}>
        {spec && <Button size="small" variant="text" onClick={() => void navigateToWorkspaceWithSpec(spec)}>Open in Query Builder</Button>}
        <Button size="small" variant="text" onClick={() => void viewInStudio(entry, pick)}>View in Studio</Button>
      </Stack>
    </Box>
  );
}

/** A catalog answer (#761): what exists — every entry with the facts the catalog holds, drafts marked, same-name
 *  metrics that mean different things flagged, and a question to ask next. Written by the server for this reader. */
export function CatalogAnswer({ ladder, onAsk, sessionId }: { ladder: LadderAnswer; onAsk?: (q: string) => void; sessionId?: string | null }) {
  const block = ladder.catalog!;
  const entries = block.entries || [];
  const byId = Object.fromEntries(entries.map((e) => [e.id, e]));
  const [expanded, setExpanded] = React.useState(false);
  const shown = expanded ? entries : entries.slice(0, COLLAPSE_AFTER);
  // the perspective the server says it answered in (a pick or an objective's, #767/#766) — never re-derived here
  const pick = ladder.scope?.used?.[0]?.id ?? null;
  return (
    <Box data-testid="catalog-answer" sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, display: 'grid', gap: 1 }}>
      {ladder.what?.headline && <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{ladder.what.headline}</Typography>}
      {/* the rest of the Writer's reply (#1669): its words, then each fact it cited — set only for the author */}
      {ladder.what?.narrative && (
        <Typography variant="body2" data-testid="catalog-narrative" sx={{ whiteSpace: 'pre-line' }}>{ladder.what.narrative}</Typography>
      )}
      {(block.conflicts || []).map((c) => (
        <Alert key={c.name} severity="warning" variant="outlined" data-testid="catalog-conflict">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>“{c.name}” means different things</Typography>
          {c.ids.map((id) => (
            <Typography key={id} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              {id}{byId[id]?.formula ? ` = ${byId[id].formula}` : ''}{byId[id]?.perspectives.length ? ` (${byId[id].perspectives.map((w) => w.name).join(', ')})` : ''}
            </Typography>
          ))}
        </Alert>
      ))}
      <Box>{shown.map((e) => <EntryRow key={`${e.kind}:${e.id}`} entry={e} pick={pick} />)}</Box>
      {entries.length > COLLAPSE_AFTER && (
        <Box><Button size="small" variant="text" data-testid="catalog-show-all" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show fewer' : `Show all ${entries.length}`}
        </Button></Box>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        {block.follow_up && (
          <Button size="small" variant="outlined" data-testid="catalog-follow-up" onClick={() => onAsk?.(block.follow_up!)}>{block.follow_up}</Button>
        )}
        {sessionId && <TraceLink sessionId={sessionId} />}
      </Stack>
    </Box>
  );
}

export default function CatalogAnswerCard({ turn }: { turn: LandingAgentTurn }) {
  const sendLandingAgentQuery = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const ladder = turn.result?.ladder as LadderAnswer | undefined;
  if (!ladder?.catalog) return null;
  // a follow-up asks about this answer: its own perspective and scope, for that question only (#767)
  return <CatalogAnswer ladder={ladder} onAsk={(q) => sendLandingAgentQuery(q, followUpOf(turn))}
                        sessionId={turn.sessionId || ladder.trace_session_id || null} />;
}
